export type XlsxCellValue = string | number | boolean | null;

const MAX_XLSX_BYTES = 25 * 1024 * 1024;
const MAX_ENTRY_BYTES = 50 * 1024 * 1024;
const END_OF_CENTRAL_DIRECTORY = 0x06054b50;
const CENTRAL_DIRECTORY_ENTRY = 0x02014b50;
const LOCAL_FILE_HEADER = 0x04034b50;

type ZipEntry = {
  name: string;
  compressionMethod: number;
  compressedSize: number;
  uncompressedSize: number;
  localHeaderOffset: number;
};

const decodeUtf8 = (bytes: Uint8Array) => new TextDecoder("utf-8").decode(bytes);

const findEndOfCentralDirectory = (view: DataView) => {
  const minimumOffset = Math.max(0, view.byteLength - 65_557);
  for (let offset = view.byteLength - 22; offset >= minimumOffset; offset -= 1) {
    if (view.getUint32(offset, true) === END_OF_CENTRAL_DIRECTORY) return offset;
  }
  throw new Error("Súbor nie je platný XLSX archív.");
};

const readZipEntries = (bytes: Uint8Array) => {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const directoryEnd = findEndOfCentralDirectory(view);
  const entryCount = view.getUint16(directoryEnd + 10, true);
  let offset = view.getUint32(directoryEnd + 16, true);
  const entries = new Map<string, ZipEntry>();

  for (let index = 0; index < entryCount; index += 1) {
    if (view.getUint32(offset, true) !== CENTRAL_DIRECTORY_ENTRY) {
      throw new Error("XLSX obsahuje poškodený ZIP adresár.");
    }
    const compressionMethod = view.getUint16(offset + 10, true);
    const compressedSize = view.getUint32(offset + 20, true);
    const uncompressedSize = view.getUint32(offset + 24, true);
    const fileNameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);
    const localHeaderOffset = view.getUint32(offset + 42, true);
    const name = decodeUtf8(bytes.subarray(offset + 46, offset + 46 + fileNameLength));

    if (uncompressedSize > MAX_ENTRY_BYTES) {
      throw new Error("XLSX obsahuje neprimerane veľký interný súbor.");
    }
    entries.set(name.replace(/^\/+/, ""), {
      name,
      compressionMethod,
      compressedSize,
      uncompressedSize,
      localHeaderOffset
    });
    offset += 46 + fileNameLength + extraLength + commentLength;
  }
  return entries;
};

const extractEntry = async (archive: Uint8Array, entry: ZipEntry) => {
  const view = new DataView(archive.buffer, archive.byteOffset, archive.byteLength);
  if (view.getUint32(entry.localHeaderOffset, true) !== LOCAL_FILE_HEADER) {
    throw new Error("XLSX obsahuje poškodenú ZIP položku.");
  }
  const fileNameLength = view.getUint16(entry.localHeaderOffset + 26, true);
  const extraLength = view.getUint16(entry.localHeaderOffset + 28, true);
  const start = entry.localHeaderOffset + 30 + fileNameLength + extraLength;
  const compressed = archive.subarray(start, start + entry.compressedSize);

  if (entry.compressionMethod === 0) return compressed;
  if (entry.compressionMethod !== 8 || typeof DecompressionStream === "undefined") {
    throw new Error("Tento prehliadač nepodporuje kompresiu použitú v XLSX súbore.");
  }

  const compressedBuffer = compressed.buffer.slice(
    compressed.byteOffset,
    compressed.byteOffset + compressed.byteLength
  ) as ArrayBuffer;
  const stream = new Blob([compressedBuffer])
    .stream()
    .pipeThrough(new DecompressionStream("deflate-raw"));
  const output = new Uint8Array(await new Response(stream).arrayBuffer());
  if (output.byteLength > MAX_ENTRY_BYTES) {
    throw new Error("XLSX obsahuje neprimerane veľký rozbalený súbor.");
  }
  return output;
};

const decodeXml = (value: string) => value
  .replace(/&#(\d+);/g, (_, code: string) => String.fromCodePoint(Number(code)))
  .replace(/&#x([0-9a-f]+);/gi, (_, code: string) => String.fromCodePoint(Number.parseInt(code, 16)))
  .replace(/&lt;/g, "<")
  .replace(/&gt;/g, ">")
  .replace(/&quot;/g, "\"")
  .replace(/&apos;/g, "'")
  .replace(/&amp;/g, "&");

const xmlText = (xml: string) => {
  const textParts = [...xml.matchAll(/<(?:[\w-]+:)?t\b[^>]*>([\s\S]*?)<\/(?:[\w-]+:)?t>/gi)];
  return textParts.map((match) => decodeXml(match[1])).join("");
};

const parseSharedStrings = (xml?: string) => {
  if (!xml) return [];
  return [...xml.matchAll(/<(?:[\w-]+:)?si\b[^>]*>([\s\S]*?)<\/(?:[\w-]+:)?si>/gi)]
    .map((match) => xmlText(match[1]));
};

const attribute = (attributes: string, name: string) => {
  const match = attributes.match(new RegExp(`(?:^|\\s)${name}="([^"]*)"`, "i"));
  return match ? decodeXml(match[1]) : undefined;
};

const columnIndex = (reference: string) => {
  const letters = reference.match(/^[A-Z]+/i)?.[0]?.toUpperCase() ?? "A";
  return [...letters].reduce((result, letter) => result * 26 + letter.charCodeAt(0) - 64, 0) - 1;
};

const parseCell = (attributes: string, content: string, sharedStrings: string[]): XlsxCellValue => {
  const type = attribute(attributes, "t");
  if (type === "inlineStr") return xmlText(content);
  const raw = content.match(/<(?:[\w-]+:)?v\b[^>]*>([\s\S]*?)<\/(?:[\w-]+:)?v>/i)?.[1];
  if (raw === undefined) return type === "str" ? xmlText(content) : null;
  const value = decodeXml(raw);
  if (type === "s") return sharedStrings[Number(value)] ?? "";
  if (type === "b") return value === "1";
  if (type === "str") return value;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : value;
};

const parseWorksheet = (xml: string, sharedStrings: string[]) => {
  const rows: XlsxCellValue[][] = [];
  for (const rowMatch of xml.matchAll(/<(?:[\w-]+:)?row\b[^>]*>([\s\S]*?)<\/(?:[\w-]+:)?row>/gi)) {
    const row: XlsxCellValue[] = [];
    for (const cellMatch of rowMatch[1].matchAll(/<(?:[\w-]+:)?c\b([^>]*?)(?:\/\s*>|>([\s\S]*?)<\/(?:[\w-]+:)?c>)/gi)) {
      const reference = attribute(cellMatch[1], "r") ?? "A1";
      row[columnIndex(reference)] = parseCell(cellMatch[1], cellMatch[2] ?? "", sharedStrings);
    }
    rows.push(row.map((value) => value ?? null));
  }
  return rows;
};

export const readFirstXlsxSheet = async (file: Blob) => {
  if (file.size === 0 || file.size > MAX_XLSX_BYTES) {
    throw new Error("XLSX súbor musí mať najviac 25 MB.");
  }
  const archive = new Uint8Array(await file.arrayBuffer());
  const entries = readZipEntries(archive);
  const worksheet = [...entries.values()]
    .filter((entry) => /^xl\/worksheets\/sheet\d+\.xml$/i.test(entry.name))
    .sort((first, second) => first.name.localeCompare(second.name, "en", { numeric: true }))[0];
  if (!worksheet) throw new Error("XLSX neobsahuje žiadny čitateľný worksheet.");

  const sharedEntry = entries.get("xl/sharedStrings.xml");
  const sharedStrings = sharedEntry
    ? parseSharedStrings(decodeUtf8(await extractEntry(archive, sharedEntry)))
    : [];
  const worksheetXml = decodeUtf8(await extractEntry(archive, worksheet));
  return parseWorksheet(worksheetXml, sharedStrings);
};
